import { useEffect, useMemo, useState, type FormEvent } from "react";
import { FileCheck2, FileSpreadsheet, Link2, Plus, Upload } from "lucide-react";
import { readSheet } from "read-excel-file/browser";

import {
  actions,
  bankStatementAttachmentUrl,
  bankStatementWorkspace,
  importBankStatement,
} from "@/lib/api";
import type { BankStatementLine } from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { FieldError } from "@/components/field-error";

type Props = {
  organisationId: string;
  refresh: number;
  payments: Record<string, any>[];
  canFinance: boolean;
  changed: () => void;
};
type Workspace = Awaited<ReturnType<typeof bankStatementWorkspace>>;
type ReviewedLine = {
  transaction_date: string;
  value_date: string;
  description: string;
  reference: string;
  direction: "debit" | "credit";
  amount: number;
  balance: string;
  raw_data?: Record<string, unknown>;
};

const ALIASES = {
  transactionDate: ["transaction date", "date", "txn date", "posting date"],
  valueDate: ["value date", "value_date"],
  description: ["description", "narration", "details", "transaction details", "particulars"],
  reference: ["reference", "transaction reference", "txn id", "transaction id", "utr", "cheque number"],
  debit: ["debit", "withdrawal", "withdrawals", "debit amount"],
  credit: ["credit", "deposit", "deposits", "credit amount"],
  amount: ["amount", "transaction amount"],
  direction: ["direction", "type", "debit credit", "dr cr"],
  balance: ["balance", "running balance", "closing balance"],
};

export function BankStatementReconciliation({
  organisationId,
  refresh,
  payments,
  canFinance,
  changed,
}: Props) {
  const [data, setData] = useState<Workspace>();
  const [error, setError] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [decisions, setDecisions] = useState<
    Record<string, { paymentId: string; amount: string; reason: string }>
  >({});
  const [busy, setBusy] = useState("");

  useEffect(() => {
    let live = true;
    bankStatementWorkspace(organisationId)
      .then((value) => {
        if (live) setData(value);
      })
      .catch((reason: Error) => {
        if (live) setError(reason.message);
      });
    return () => {
      live = false;
    };
  }, [organisationId, refresh]);

  const unmatched = useMemo(
    () => data?.lines.filter((line) => line.match_status === "unmatched") || [],
    [data],
  );
  const paidPayments = payments.filter((row) => row.state === "paid");

  async function decide(
    line: BankStatementLine,
    decision: "match" | "ignore",
  ) {
    const input = decisions[line.id] || {
      paymentId: "",
      amount: String(line.amount),
      reason: "",
    };
    if (!input.reason.trim()) return;
    setBusy(line.id + decision);
    setError("");
    try {
      if (decision === "match")
        await actions.matchBankStatementLine(
          organisationId,
          line.id,
          input.paymentId,
          Number(input.amount),
          input.reason,
        );
      else
        await actions.ignoreBankStatementLine(
          organisationId,
          line.id,
          input.reason,
        );
      changed();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy("");
    }
  }

  async function openEvidence(objectPath: string) {
    try {
      const url = await bankStatementAttachmentUrl(objectPath);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (reason) {
      setError((reason as Error).message);
    }
  }

  return (
    <section className="surface overflow-hidden">
      <header className="section-heading">
        <span className="section-heading__icon">
          <FileCheck2 className="size-4" />
        </span>
        <div className="flex-1">
          <h2>Bank statement evidence</h2>
          <p>
            Import reviewed bank lines and match them to RAQOZ-recorded paid
            payments. The bank file remains private original evidence; RAQOZ
            still does not move money or read a live bank feed.
          </p>
        </div>
        {canFinance && (
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            <Upload className="size-4" />
            Import statement
          </Button>
        )}
      </header>

      {error && (
        <p className="m-5 rounded-xl bg-red-50 p-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      <div className="grid gap-5 p-5 xl:grid-cols-[.8fr_1.2fr]">
        <div>
          <p className="text-xs font-black tracking-[.12em] text-[var(--text-muted)]">
            PRIVATE SOURCE FILES
          </p>
          <div className="mt-3 grid gap-2">
            {(data?.statements || []).map((statement) => (
              <div
                key={statement.id}
                className="rounded-xl border border-[var(--border)] p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <span>
                    <strong className="block text-sm">{statement.account_label}</strong>
                    <small className="text-[var(--text-secondary)]">
                      {titleCase(statement.source_format)} · {statement.currency} ·{" "}
                      {statement.statement_period_start
                        ? displayDate(statement.statement_period_start)
                        : "Period not supplied"}
                    </small>
                  </span>
                  <Badge value={statement.status} />
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <small className="truncate text-[var(--text-muted)]">
                    SHA-256 {statement.original_sha256.slice(0, 16)}…
                  </small>
                  {statement.attachments?.object_path && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        openEvidence(statement.attachments!.object_path)
                      }
                    >
                      Original
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {!data?.statements.length && (
              <p className="text-sm text-[var(--text-secondary)]">
                No bank statement evidence has been uploaded.
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <p className="text-xs font-black tracking-[.12em] text-[var(--text-muted)]">
                HUMAN-CONFIRMED MATCHING
              </p>
              <h3 className="mt-1 font-serif text-xl">Unmatched bank lines</h3>
            </div>
            <Badge value={unmatched.length ? "action required" : "current"} />
          </div>
          <div className="mt-3 grid gap-3">
            {unmatched.map((line) => {
              const input = decisions[line.id] || {
                paymentId: "",
                amount: String(line.amount),
                reason: "",
              };
              const eligible = paidPayments.filter(
                (payment) => payment.currency === line.currency,
              );
              return (
                <div
                  key={line.id}
                  className="rounded-xl border border-[var(--border)] p-4"
                >
                  <div className="flex justify-between gap-3">
                    <span>
                      <strong className="block text-sm">
                        {line.description}
                      </strong>
                      <small className="text-[var(--text-secondary)]">
                        {displayDate(line.transaction_date)} ·{" "}
                        {line.transaction_reference || "No transaction reference"}
                      </small>
                    </span>
                    <strong className={line.direction === "debit" ? "text-red-700" : "text-emerald-700"}>
                      {titleCase(line.direction)} {money(line.amount, line.currency)}
                    </strong>
                  </div>
                  {canFinance && (
                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <Label>
                        Paid RAQOZ record
                        <Select
                          value={input.paymentId}
                          onChange={(event) =>
                            setDecisions((current) => ({
                              ...current,
                              [line.id]: {
                                ...input,
                                paymentId: event.target.value,
                              },
                            }))
                          }
                        >
                          <option value="">Select payment</option>
                          {eligible.map((payment) => (
                            <option key={payment.payment_id} value={payment.payment_id}>
                              {payment.invoice_no} · {payment.vendor_name} ·{" "}
                              {money(payment.net_amount, payment.currency)}
                            </option>
                          ))}
                        </Select>
                      </Label>
                      <Label>
                        Statement amount
                        <Input
                          type="number"
                          readOnly
                          value={input.amount}
                        />
                      </Label>
                      <Label className="md:col-span-2">
                        Decision reason
                        <Input
                          value={input.reason}
                          placeholder="Why this line matches, or why it should be ignored"
                          onChange={(event) =>
                            setDecisions((current) => ({
                              ...current,
                              [line.id]: { ...input, reason: event.target.value },
                            }))
                          }
                        />
                      </Label>
                      <div className="flex gap-2 md:col-span-2">
                        <Button
                          size="sm"
                          disabled={
                            line.direction !== "debit" ||
                            !input.paymentId ||
                            !input.reason.trim() ||
                            busy === line.id + "match"
                          }
                          onClick={() => decide(line, "match")}
                        >
                          <Link2 className="size-4" />
                          Confirm match
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={
                            !input.reason.trim() ||
                            busy === line.id + "ignore"
                          }
                          onClick={() => decide(line, "ignore")}
                        >
                          Ignore with reason
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {!unmatched.length && (
              <p className="rounded-xl bg-emerald-50 p-4 text-sm text-emerald-800">
                No reviewed bank line is waiting for a matching decision.
              </p>
            )}
          </div>
        </div>
      </div>

      <BankStatementImportDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        organisationId={organisationId}
        onDone={changed}
      />
    </section>
  );
}

function BankStatementImportDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: string;
  onDone: () => void;
}) {
  const [file, setFile] = useState<File>();
  const [format, setFormat] = useState<"csv" | "xlsx" | "pdf">("csv");
  const [lines, setLines] = useState<ReviewedLine[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function chooseFile(next?: File) {
    setFile(next);
    setError("");
    setLines([]);
    if (!next) return;
    try {
      const extension = next.name.split(".").pop()?.toLowerCase();
      if (!["csv", "xlsx", "pdf"].includes(extension || ""))
        throw new Error("Choose a CSV, XLSX or PDF bank statement.");
      const nextFormat = extension as "csv" | "xlsx" | "pdf";
      setFormat(nextFormat);
      if (nextFormat === "pdf") return;
      const rows =
        nextFormat === "csv"
          ? parseCsv(await next.text())
          : (await readSheet(next)).map((row) => row.map(cellText));
      setLines(normalizeStatementRows(rows));
    } catch (reason) {
      setError((reason as Error).message);
    }
  }

  function addLine() {
    setLines((current) => [
      ...current,
      {
        transaction_date: new Date().toISOString().slice(0, 10),
        value_date: "",
        description: "",
        reference: "",
        direction: "debit",
        amount: 0,
        balance: "",
      },
    ]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file) return;
    if (
      !lines.length ||
      lines.some(
        (line) =>
          !line.transaction_date ||
          !line.description.trim() ||
          Number(line.amount) <= 0,
      )
    ) {
      setError("Review every line and provide its date, description and positive amount.");
      return;
    }
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await importBankStatement(organisationId, file, {
        sourceFormat: format,
        accountLabel: String(form.get("accountLabel")),
        periodStart: String(form.get("periodStart") || ""),
        periodEnd: String(form.get("periodEnd") || ""),
        currency: String(form.get("currency") || "INR"),
        parsingNote:
          format === "pdf"
            ? "Original PDF retained; statement lines manually entered and confirmed by finance."
            : "Spreadsheet parsed in the browser; every line reviewed and confirmed by finance.",
        lines: lines.map((line) => ({
          ...line,
          raw_data: line.raw_data || line,
        })),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(980px,calc(100vw-2rem))]">
        <DialogHeader
          eyebrow="PRIVATE FINANCE EVIDENCE"
          title="Import a bank statement"
          description="CSV/XLSX lines are parsed locally for review. A PDF is retained as original evidence and requires manual line entry; RAQOZ does not claim PDF extraction or bank confirmation."
        />
        <form onSubmit={submit} className="grid max-h-[78vh] gap-5 overflow-auto p-6">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Label className="md:col-span-2">
              Statement file
              <Input
                type="file"
                accept=".csv,.xlsx,.pdf,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => chooseFile(event.target.files?.[0])}
                required
              />
            </Label>
            <Label>
              Account label
              <Input name="accountLabel" placeholder="Operating account ending 1234" required />
            </Label>
            <Label>
              Currency
              <Input name="currency" defaultValue="INR" pattern="[A-Za-z]{3}" maxLength={3} required />
            </Label>
            <Label>
              Period start
              <Input name="periodStart" type="date" />
            </Label>
            <Label>
              Period end
              <Input name="periodEnd" type="date" />
            </Label>
          </div>

          {file && (
            <div className="rounded-xl border border-[var(--border)] p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="flex items-center gap-2 text-sm">
                  <FileSpreadsheet className="size-4 text-emerald-700" />
                  <strong>{file.name}</strong>
                  <Badge value={format} />
                </span>
                <Button type="button" size="sm" variant="outline" onClick={addLine}>
                  <Plus className="size-4" />
                  Add manual line
                </Button>
              </div>
              {format === "pdf" && !lines.length && (
                <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                  PDF extraction is not assumed. Add the outgoing payment lines that must be reconciled; the original PDF remains attached for verification.
                </p>
              )}
            </div>
          )}

          {!!lines.length && (
            <div>
              <div className="mb-2 flex items-end justify-between">
                <div>
                  <p className="text-xs font-black tracking-[.12em] text-[var(--text-muted)]">REVIEW BEFORE COMMIT</p>
                  <h3 className="mt-1 font-serif text-xl">{lines.length} statement lines</h3>
                </div>
                <small className="text-[var(--text-secondary)]">No automatic match occurs on import.</small>
              </div>
              <div className="max-h-80 overflow-auto rounded-xl border border-[var(--border)]">
                {lines.map((line, index) => (
                  <div key={index} className="grid gap-2 border-b border-[var(--border)] p-3 last:border-0 md:grid-cols-[130px_110px_1fr_150px_120px_40px]">
                    <Input
                      type="date"
                      value={line.transaction_date}
                      onChange={(event) => updateLine(lines, setLines, index, "transaction_date", event.target.value)}
                      aria-label={`Line ${index + 1} date`}
                    />
                    <Select
                      value={line.direction}
                      onChange={(event) => updateLine(lines, setLines, index, "direction", event.target.value)}
                      aria-label={`Line ${index + 1} direction`}
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </Select>
                    <Input
                      value={line.description}
                      onChange={(event) => updateLine(lines, setLines, index, "description", event.target.value)}
                      placeholder="Description"
                      aria-label={`Line ${index + 1} description`}
                    />
                    <Input
                      value={line.reference}
                      onChange={(event) => updateLine(lines, setLines, index, "reference", event.target.value)}
                      placeholder="Reference / UTR"
                      aria-label={`Line ${index + 1} reference`}
                    />
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={line.amount}
                      onChange={(event) => updateLine(lines, setLines, index, "amount", Number(event.target.value))}
                      aria-label={`Line ${index + 1} amount`}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setLines((current) => current.filter((_, rowIndex) => rowIndex !== index))}
                      aria-label={`Remove line ${index + 1}`}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !file || !lines.length}>
              {busy ? "Sealing statement evidence…" : "Confirm and import reviewed lines"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function updateLine(
  lines: ReviewedLine[],
  setLines: (lines: ReviewedLine[]) => void,
  index: number,
  field: keyof ReviewedLine,
  value: unknown,
) {
  setLines(
    lines.map((line, rowIndex) =>
      rowIndex === index ? { ...line, [field]: value } : line,
    ),
  );
}

function normalizeHeader(value: unknown) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

function findColumn(headers: unknown[], aliases: string[]) {
  const normalized = headers.map(normalizeHeader);
  return normalized.findIndex((header) =>
    aliases.some((alias) => header === normalizeHeader(alias)),
  );
}

function numeric(value: unknown) {
  const text = String(value ?? "")
    .trim()
    .replace(/,/g, "")
    .replace(/^\((.*)\)$/, "-$1");
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

function isoDate(value: unknown) {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (typeof value === "number" && value > 1) {
    const date = new Date(Date.UTC(1899, 11, 30) + value * 86400000);
    return date.toISOString().slice(0, 10);
  }
  const text = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const local = text.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (local)
    return `${local[3]}-${local[2].padStart(2, "0")}-${local[1].padStart(2, "0")}`;
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}

function normalizeStatementRows(rows: unknown[][]): ReviewedLine[] {
  if (rows.length < 2) throw new Error("The statement has no transaction rows.");
  const headers = rows[0];
  const columns = Object.fromEntries(
    Object.entries(ALIASES).map(([key, aliases]) => [
      key,
      findColumn(headers, aliases),
    ]),
  ) as Record<keyof typeof ALIASES, number>;
  if (
    columns.transactionDate < 0 ||
    columns.description < 0 ||
    (columns.debit < 0 && columns.credit < 0 && columns.amount < 0)
  )
    throw new Error(
      "Could not identify the date, description and amount columns. Use canonical headings or upload the PDF and enter reviewed lines manually.",
    );
  const lines = rows.slice(1).flatMap((row) => {
    const debit = columns.debit >= 0 ? numeric(row[columns.debit]) : 0;
    const credit = columns.credit >= 0 ? numeric(row[columns.credit]) : 0;
    const signed = columns.amount >= 0 ? numeric(row[columns.amount]) : 0;
    const directionText =
      columns.direction >= 0
        ? String(row[columns.direction] || "").toLowerCase()
        : "";
    const direction: "debit" | "credit" =
      debit > 0 ||
      signed < 0 ||
      /debit|withdrawal|\bdr\b/.test(directionText)
        ? "debit"
        : "credit";
    const amount = Math.abs(debit || credit || signed);
    const transactionDate = isoDate(row[columns.transactionDate]);
    const description = String(row[columns.description] || "").trim();
    if (!transactionDate || !description || amount <= 0) return [];
    return [
      {
        transaction_date: transactionDate,
        value_date:
          columns.valueDate >= 0 ? isoDate(row[columns.valueDate]) : "",
        description,
        reference:
          columns.reference >= 0
            ? String(row[columns.reference] || "").trim()
            : "",
        direction,
        amount,
        balance:
          columns.balance >= 0
            ? String(numeric(row[columns.balance]) || "")
            : "",
        raw_data: Object.fromEntries(
          headers.map((header, index) => [String(header), row[index]]),
        ),
      },
    ];
  });
  if (!lines.length)
    throw new Error("No valid transaction lines were found for review.");
  if (lines.length > 5000)
    throw new Error("A statement import is limited to 5,000 lines.");
  return lines;
}

function cellText(value: unknown) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value;
  return String(value).trim();
}

function parseCsv(content: string) {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let index = 0; index < content.length; index += 1) {
    const character = content[index];
    if (quoted) {
      if (character === '"' && content[index + 1] === '"') {
        cell += '"';
        index += 1;
      } else if (character === '"') quoted = false;
      else cell += character;
    } else if (character === '"') quoted = true;
    else if (character === ",") {
      row.push(cell);
      cell = "";
    } else if (character === "\n") {
      row.push(cell.replace(/\r$/, ""));
      rows.push(row);
      row = [];
      cell = "";
    } else cell += character;
  }
  row.push(cell.replace(/\r$/, ""));
  if (row.some(Boolean)) rows.push(row);
  if (quoted) throw new Error("The CSV contains an unclosed quoted cell.");
  return rows;
}
